import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import MedicalReviewByline from '../components/MedicalReviewByline';
import {
  ChevronRight, ChevronDown, ArrowRight, HeartHandshake, Utensils,
  CalendarCheck, ShieldCheck, Stethoscope, HelpCircle, CheckCircle,
} from 'lucide-react';

const principles = [
  { icon: Utensils, title: 'A balanced plate, not restriction', body: 'Vegetables, a protein source, a measured portion of carbohydrate (rice, roti or millets) and some healthy fats. Familiar home-style Indian food, balanced sensibly, usually works better than extreme diets.' },
  { icon: CalendarCheck, title: 'Consistency and regular meals', body: 'Eating at reasonably regular times with steady portions can help many older adults manage blood glucose, especially if they take medication. Skipping meals is best avoided.' },
  { icon: HeartHandshake, title: 'Respect their preferences', body: 'Changes are easier to sustain when they fit your parent\u2019s tastes, culture and routine. Small, practical swaps tend to last longer than a complete overhaul.' },
  { icon: ShieldCheck, title: 'Food supports, it does not replace treatment', body: 'Nutrition works alongside prescribed medication and regular monitoring. Never stop or change medication based on diet alone \u2014 that decision belongs to their treating doctor.' },
];

const mealIdeas = [
  'Breakfast: vegetable besan chilla, moong dal chilla, idli with sambar, or oats with nuts',
  'Lunch: 1\u20132 roti or a measured portion of rice, dal or rajma, a large vegetable sabzi and salad',
  'Dinner: light and early where possible \u2014 vegetables, a protein source and a small carbohydrate portion',
  'Snacks: curd, a handful of nuts, roasted chana, or a small portion of whole fruit',
];

const faqs = [
  { question: 'My parents have diabetes. What should they eat?', answer: 'A balanced plate built from everyday Indian foods usually works well: plenty of vegetables, a protein source such as dal or curd, a measured portion of carbohydrate like rice, roti or millets, and some healthy fats. Portion size, consistency and pairing carbohydrate with protein and fibre matter more than banning single foods. Personalize with their doctor, especially if they take medication.' },
  { question: 'How can I help my parents manage diabetes with food?', answer: 'Support steady meal times and sensible portions, keep plenty of vegetables and protein at home, limit sugary drinks and ultra-processed snacks, and encourage gentle regular activity such as walking. Keep up their medical check-ups and monitoring. Food supports blood sugar and weight management; it complements, and never replaces, medical treatment.' },
  { question: 'Can food alone control or cure my parent\u2019s diabetes?', answer: 'No. Nutrition is an important part of diabetes care and can support blood glucose and weight management, but it should not be expected to cure diabetes or replace prescribed medication. Any medication change must be made only by their treating doctor.' },
  { question: 'Is an Indian diet suitable for diabetic parents?', answer: 'Yes. Dal, vegetables, curd, millets and measured portions of rice or roti can form the basis of a practical, culturally familiar diabetes-friendly diet. The key is balance and portion awareness rather than giving up favourite foods entirely.' },
  { question: 'What foods should diabetic elderly parents limit?', answer: 'Sugary drinks, sweets, fried and ultra-processed snacks are best limited. Very large portions of refined carbohydrates are also worth moderating. Focus on vegetables, protein, fibre and balanced meals, while keeping food enjoyable and sustainable.' },
];

const DiabetesForParentsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const url = '/diabetes-diet-for-parents';

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Diabetes Diet for Parents: How to Help Mother or Father Eat Well | TCUBE FITNESS™</title>
        <meta name="description" content="Worried about a parent with diabetes? A doctor-informed guide to a practical Indian diabetes diet for parents, with meal ideas and how to help your mother or father manage blood sugar alongside medical care." />
        <meta name="keywords" content="diabetes diet for parents, diabetes food for parents, diabetes control for parents, diabetic diet for parents, Indian diabetic diet for parents, diabetes diet for mother, diabetes diet for father, diabetes care for elderly parents, help parents manage diabetes" />
        <link rel="canonical" href="https://tcubefitness.com/diabetes-diet-for-parents" />
      </Helmet>

      <SEOSchema
        type="article"
        name="Diabetes Diet for Parents"
        description="A doctor-informed guide to a practical Indian diabetes diet for parents and how to help a mother or father manage blood sugar alongside medical care."
        url={url}
        article={{
          headline: 'Diabetes Diet for Parents: How to Help Your Mother or Father Eat Well',
          author: 'Dr. Pooja Sree',
          datePublished: '2026-06-20',
          dateModified: '2026-06-20',
        }}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Diabetes Diet for Parents', path: url },
        ]}
        faq={faqs}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16" data-testid="diabetes-parents-page">
        <div className="container mx-auto px-4 sm:px-6">

          <nav aria-label="Breadcrumb" className="mb-6 text-xs sm:text-sm">
            <ol className="flex items-center gap-1.5 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
              <li className="text-slate-300">Diabetes Diet for Parents</li>
            </ol>
          </nav>

          {/* Hero */}
          <section className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-5">
              <Stethoscope className="w-4 h-4" aria-hidden="true" /> Doctor-Led Diabetes Nutrition
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Helping Your Parents <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Manage Diabetes With Food</span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-6">
              If your mother or father has diabetes, you are probably asking a simple, caring question: what should they eat? This doctor-informed guide shares practical, everyday Indian nutrition principles to support blood sugar management, alongside their medical care.
            </p>
            <div className="flex justify-center"><MedicalReviewByline /></div>
          </section>

          {/* What should they eat */}
          <section className="max-w-3xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">"My parents have diabetes — what should they eat?"</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              There is no single diabetes diet. The right approach depends on your parent's medical history, medications, blood glucose patterns, weight and food preferences. That said, a few balanced principles help most families get started.
            </p>
          </section>

          {/* Principles */}
          <section className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-5 mb-14" aria-label="Diabetes nutrition principles">
            {principles.map((p) => (
              <div key={p.title} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6" data-testid={`parents-principle-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 mb-4">
                  <p.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </section>

          {/* Meal ideas */}
          <section className="max-w-3xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5">Simple Indian meal ideas</h2>
            <div className="bg-gradient-to-r from-blue-900/20 to-slate-900/20 border border-blue-500/20 rounded-2xl p-6">
              <ul className="space-y-3">
                {mealIdeas.map((m, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm sm:text-base">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-slate-500 text-sm mt-4">These are general examples, not a prescription. Portions and choices should be personalized, especially if your parent takes medication.</p>
          </section>

          {/* What food cannot do */}
          <section className="max-w-3xl mx-auto mb-14">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6" role="note">
              <h2 className="text-sm sm:text-base font-bold text-amber-200 mb-2 uppercase tracking-wider">Important</h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Food supports blood sugar and weight management but does not cure diabetes and does not replace medical treatment. Please keep up your parent's prescribed medication, regular monitoring and doctor reviews. Any medication change must be made only by their treating doctor.
              </p>
            </div>
          </section>

          {/* Internal links */}
          <section className="max-w-3xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-5">Read more &amp; take the next step</h2>
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <ul className="space-y-2.5">
                {[
                  { label: 'Full Indian Diabetes Diet Plan: foods, meals and portions', to: '/blog/diabetes-diet-plan-indian-foods' },
                  { label: 'Rice, Roti and Fruit in Diabetes: what you really need to know', to: '/blog/rice-roti-fruit-and-diabetes' },
                  { label: 'Our Doctor-Led Diabetes & Clinical Nutrition program', to: '/medical-nutrition-therapy' },
                  { label: 'Start a Full Body Health Analysis for your parent', to: '/health-analysis' },
                ].map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="inline-flex items-start gap-2 text-blue-400 hover:underline underline-offset-4 font-medium" data-testid={`parents-link-${l.to.split('/').pop()}`}>
                      <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{l.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto mb-14" aria-labelledby="parents-faq-heading">
            <div className="flex items-center gap-2.5 mb-6">
              <HelpCircle className="w-5 h-5 text-blue-400" aria-hidden="true" />
              <h2 id="parents-faq-heading" className="text-2xl sm:text-3xl font-bold text-white">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <div key={i} className={`bg-slate-900/50 rounded-2xl border ${openFaq === i ? 'border-blue-500/40' : 'border-slate-800'} transition-colors`}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                    className="w-full flex items-center justify-between gap-3 p-5 text-left"
                    data-testid={`parents-faq-toggle-${i}`}
                  >
                    <span className="text-white font-semibold text-sm sm:text-base pr-4">{f.question}</span>
                    <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180 text-blue-400' : 'text-slate-500'}`} aria-hidden="true" />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-[600px]' : 'max-h-0'}`}>
                    <p className="px-5 pb-5 text-slate-400 text-sm sm:text-base leading-relaxed">{f.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="max-w-4xl mx-auto bg-gradient-to-br from-blue-600 to-cyan-600 rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-slate-950/75" aria-hidden="true"></div>
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Get a plan built around your parent's health</h2>
              <p className="text-slate-300 mb-6 max-w-xl mx-auto text-sm sm:text-base">
                Our medical team can create a practical, personalized diabetes nutrition plan that fits your parent's medications, reports and food preferences, alongside their doctor.
              </p>
              <Link to="/book-consultation?from=diabetes-parents" className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all" data-testid="parents-cta-book">
                Book a Diabetes Nutrition Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </section>

        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default DiabetesForParentsPage;
